/**
 * Wd local environment service (wd-local-environment.service.ts).
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @author Deitric Jang <djang@buildcentrix.com>
 * @author Jenny Lam <jlam@buildcentrix.com>
 * @copyright (c) 2021-2024 Build Centrix
 */
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  IEnvironmentConfiguration,
  IEnvironmentConstants,
  IEnvironmentModel,
  IEnvironmentPermissions,
  IEnvironmentResources,
} from '../../interfaces/environment/environment-interfaces';

@Injectable({
  providedIn: 'root',
})
export class WdLocalEnvironmentService {
  public isInitialized = new BehaviorSubject(false);

  /**
   * Initialized Configuration Values
   * @since 4.37.0
   */
  configuration: IEnvironmentConfiguration = {};
  /**
   * Initialized Constants
   * @since 4.37.0
   */
  constants: IEnvironmentConstants = {};
  /**
   * Initialized Permissions
   * @since 4.37.0
   */
  permissions: IEnvironmentPermissions = {};
  /**
   * Initialized Resources
   * @since 4.37.0
   */
  resources: IEnvironmentResources = {};
  /**
   * Initialized User Values
   * @since 4.37.0
   */
  user: any = {};

  // Environment Values
  public values: IEnvironmentModel;

  // Date Format
  dateFormat = 'MMM D, YYYY';
  datePipeFormat = 'MMM dd, YYYY';

  /**
   * Constructor Initialization
   * @since 4.37.0
   */
  constructor(
    @Inject('wdEnvironmentService') private wdEnvironmentService: any,
    private translate: TranslateService,
  ) {}

  /**
   * Initialize resources that comes for wd-library-environment
   *
   * @since 4.63.0
   *
   */
  async initialize(): Promise<void> {
    this.values = await this.wdEnvironmentService.initialize();
    // Set translation language
    this.translate.use(this.values.constants.getLocaleLanguage());

    // Initialize Configuration
    this.configuration = this.initializeConfiguration(
      this.wdEnvironmentService.configuration,
    );
    // Initialize Constants
    this.constants = this.initializeConstants(
      this.wdEnvironmentService.constants,
    );
    // Initialize Permissions
    this.permissions = this.initializePermissions(
      this.wdEnvironmentService.permissions,
    );
    // Initialize Resources
    this.resources = this.initializeResources(
      this.wdEnvironmentService.resources,
    );
    // Initialize User
    this.user = this.initializeUser(this.wdEnvironmentService.user);

    // Set Initialized Flag
    this.isInitialized.next(true);
  }

  /**
   * Initialize Configuration
   * Note: The use of function calls within the HTML template causes UI lag
   * In order to avoid the digest cycle repeating the function calls we need to initialize the configuration first
   * See: https://lukeliutingchun.medium.com/angular-performance-issue-caused-by-function-calls-in-template-a1a930f40464#742c
   * @since 4.37.0
   */
  private initializeConfiguration(values: any) {
    const results: any = {};
    results.getBaseApiPath = values.getBaseApiPath();
    results.getDataExportApiPath = values.getDataExportApiPath();
    results.getMaximumFileSize = values.getMaximumFileSize();
    results.getMaximumFileUploads = values.getMaximumFileUploads();
    results.getMaximumFilePostSize = values.getMaximumFilePostSize();
    results.getOrderApiPath = values.getOrderApiPath();
    results.getPublicApiPath = values.getPublicApiPath();
    results.getManufacturingAdminApiPath = values.getManufacturingAdminApiPath();
    results.getUserApiPath = values.getUserApiPath();
    results.getSpoolingApiPath = values.getSpoolingApiPath();
    return results;
  }

  /**
   * Initialize Constants
   * Note: The use of function calls within the HTML template causes UI lag
   * In order to avoid the digest cycle repeating the function calls we need to initialize the constants first
   * See: https://lukeliutingchun.medium.com/angular-performance-issue-caused-by-function-calls-in-template-a1a930f40464#742c
   * @since 4.37.0
   */
  private initializeConstants(values: any) {
    const results: any = {};
    results.getCompanyMinimumDaysRequired = values.getCompanyMinimumDaysRequired();
    results.getCompanyName = values.getCompanyName();
    results.getDefaultCamSystem = values.getDefaultCamSystem();
    results.getLocaleCurrencyUnit = values.getLocaleCurrencyUnit();
    results.getLocaleLanguage = values.getLocaleLanguage();
    results.getLocaleWeightUnit = values.getLocaleWeightUnit();
    results.isPONumberRequired = values.isPONumberRequired();
    results.isTagRequired = values.isTagRequired();
    results.getDeliverableCountries = values.getDeliverableCountries();
    results.isCustomOrderFormFieldsEnabled = values.isCustomOrderFormFieldsEnabled();
    return results;
  }

  /**
   * Initialize Permissions
   * Note: The use of function calls within the HTML template causes UI lag
   * In order to avoid the digest cycle repeating the function calls we need to initialize the permissions first
   * See: https://lukeliutingchun.medium.com/angular-performance-issue-caused-by-function-calls-in-template-a1a930f40464#742c
   */
  private initializePermissions(values: any) {
    const results: any = {};
    results.canAddOrderMessages = values.canAddOrderMessages();
    results.canChangeDeliveryDate = values.canChangeDeliveryDate();
    results.canChangeDeliveryStatus = values.canChangeDeliveryStatus();
    results.canChangeFulfillmentGroupStatus = values.canChangeFulfillmentGroupStatus();
    results.canChangeOrderStatus = values.canChangeOrderStatus();
    results.canChangeProductionDate = values.canChangeProductionDate();
    results.canChangeProductionStatus = values.canChangeProductionStatus();
    results.canDeleteOrderMessages = values.canDeleteOrderMessages();
    results.canEditEcommerceJob = values.canEditEcommerceJob();
    results.canEditOrderDetails = values.canEditOrderDetails();
    results.canEditOrderMessages = values.canEditOrderMessages();
    results.canManageAdjustments = values.canManageAdjustments();
    results.canManageWorkOrders = values.canManageWorkOrders();
    results.canSubmitOrdersOnBehalf = values.canSubmitOrdersOnBehalf();
    results.canViewAdjustments = values.canViewAdjustments();
    results.canViewCamductExport = values.canViewCamductExport();
    results.canViewCombinedCsvExport = values.canViewCombinedCsvExport();
    results.canViewFabricationTime = values.canViewFabricationTime();
    results.canViewWeight = values.canViewWeight();
    results.canViewPricing = values.canViewPricing();
    results.canViewFieldPdfExport = values.canViewFieldPdfExport();
    results.canViewFulfillmentCsvExport = values.canViewFulfillmentCsvExport();
    results.canViewFulfillmentPdfExport = values.canViewFulfillmentPdfExport();
    results.canViewWorkOrderBillingReport = values.canViewWorkOrderBillingReport();
    results.canViewWorkOrderShopReportModern = values.canViewWorkOrderShopReportModern();
    results.canViewWorkOrderSupplyOnlyReport = values.canViewWorkOrderSupplyOnlyReport();
    results.canViewVehicleAssignments = values.canViewVehicleAssignments();
    return results;
  }

  /**
   * Initialize Resources
   * @since 4.37.0
   */
  private initializeResources(values: any) {
    return values;
  }

  /**
   * Initialize User
   * Note: The use of function calls within the HTML template causes UI lag
   * In order to avoid the digest cycle repeating the function calls we need to initialize the user first
   * See: https://lukeliutingchun.medium.com/angular-performance-issue-caused-by-function-calls-in-template-a1a930f40464#742c
   * @since 4.37.0
   */
  private initializeUser(values: any) {
    return values.getInformation();
  }
}
